import { useState, useEffect } from 'react';
import PolicyViolationsService from '../../../../../../services/PolicyViolations';
import PerformanceVerbalWarningService from '../../../../../../services/PerformanceVerbalWarning';
import { INITIAL_FORM_DATA } from './formConstants';
import { UserWithEmployee } from '../../UserSelectionModal';

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForVerbalWarning: string;
  nextConsequences: string;
  expectationOfImprovement: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  date?: string;
  policyViolationIds?: string;
  reasonForVerbalWarning?: string;
  nextConsequences?: string;
  expectationOfImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
  action_taken?: string;
}

export const useVerbalWarningForm = (
  isOpen: boolean,
  token: string,
  currentUser?: UserWithEmployee,
  submittedForms?: Map<string, FormData>,
  editMode: boolean = false,
  verbalWarningId?: string,
) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [policyViolations, setPolicyViolations] = useState<PolicyViolation[]>([]);
  const [priorDiscussions, setPriorDiscussions] = useState<PriorDiscussion[]>([]);

  const loadPolicyViolations = async () => {
    try {
      const service = PolicyViolationsService(token);
      const response = await service.list();
      setPolicyViolations(response.data || []);
    } catch (error) {
      console.error('Error loading policy violations:', error);
      setPolicyViolations([]);
    }
  };

  const loadPriorDiscussions = async (userId: string) => {
    try {
      const service = PerformanceVerbalWarningService(token);
      const response = await service.getPriorDiscussions(userId);
      setPriorDiscussions(response.data || []);
    } catch (error) {
      console.error('Error loading prior discussions:', error);
      setPriorDiscussions([]);
    }
  };

  const loadVerbalWarningData = async (id: string) => {
    try {
      const service = PerformanceVerbalWarningService(token);
      const response = await service.show(id);
      const verbalWarning = response.data;

      if (verbalWarning) {
        let policyViolationIds: string[] = [];

        if (verbalWarning.policyViolationIds && Array.isArray(verbalWarning.policyViolationIds)) {
          policyViolationIds = verbalWarning.policyViolationIds;
        } else if (verbalWarning.policyViolations && Array.isArray(verbalWarning.policyViolations)) {
          policyViolationIds = verbalWarning.policyViolations.map((pv: any) => pv.id || pv);
        } else if (verbalWarning.policyViolationId) {
          policyViolationIds = [verbalWarning.policyViolationId];
        } else if (verbalWarning.policyViolation?.id) {
          policyViolationIds = [verbalWarning.policyViolation.id];
        }

        setFormData({
          name: verbalWarning.name || '',
          date: verbalWarning.date ? new Date(verbalWarning.date).toISOString().split('T')[0] : '',
          position: verbalWarning.position || '',
          department: verbalWarning.department || '',
          supervisor: verbalWarning.supervisor || '',
          dateOfHire: verbalWarning.dateOfHire ? new Date(verbalWarning.dateOfHire).toISOString().split('T')[0] : '',
          policyViolationIds,
          reasonForVerbalWarning: verbalWarning.reasonForVerbalWarning || '',
          nextConsequences: verbalWarning.nextConsequences || '',
          expectationOfImprovement: verbalWarning.expectationOfImprovement || '',
          privateNotes: verbalWarning.privateNotes || '',
        });

        if (verbalWarning.userId) {
          loadPriorDiscussions(verbalWarning.userId);
        }
      }
    } catch (error) {
      console.error('Error loading verbal warning data:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadPolicyViolations();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && editMode && verbalWarningId && token) {
      loadVerbalWarningData(verbalWarningId);
    }
  }, [isOpen, editMode, verbalWarningId, token]);

  useEffect(() => {
    if (isOpen && !editMode && currentUser) {
      const savedForm = submittedForms?.get(currentUser.id);
      if (savedForm) {
        setFormData(savedForm);
      } else {
        const today = new Date().toISOString().split('T')[0];
        const lastSavedForm =
          submittedForms && submittedForms.size > 0 ? Array.from(submittedForms.values()).at(-1) : undefined;
        setFormData({
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          date: today,
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '',
          policyViolationIds: lastSavedForm?.policyViolationIds ?? [],
          reasonForVerbalWarning: lastSavedForm?.reasonForVerbalWarning ?? '',
          nextConsequences: lastSavedForm?.nextConsequences ?? '',
          expectationOfImprovement: lastSavedForm?.expectationOfImprovement ?? '',
          privateNotes: lastSavedForm?.privateNotes ?? '',
        });
      }
      setErrors({});
      loadPriorDiscussions(currentUser.id);
    }
  }, [isOpen, editMode, currentUser]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setPriorDiscussions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else if (formData.date > today) {
      newErrors.date = 'Date cannot be in the future';
    }
    if (formData.policyViolationIds.length === 0) {
      newErrors.policyViolationIds = 'At least one Policy Violation is required';
    }
    if (!formData.reasonForVerbalWarning.trim()) {
      newErrors.reasonForVerbalWarning = 'Reason for Verbal Warning is required';
    }
    if (!formData.nextConsequences.trim()) {
      newErrors.nextConsequences = 'Next Consequences is required';
    }
    if (!formData.expectationOfImprovement.trim()) {
      newErrors.expectationOfImprovement = 'Expectation of Improvement is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    policyViolations,
    priorDiscussions,
    handleInputChange,
    validateForm,
    setFormData,
  };
};
